import { Box } from 'app/components/design-system-components'
import { CheckboxesList } from 'app/components/design-system-components/list-filters/CheckboxesList'
import { DropdownBodySectionHeader } from 'app/components/design-system-components/list-filters/DropdownBodySectionHeader'
import { theme } from 'app/match/applicationReportPDF/assets/theme'

const mgmtTypeFilterFields = {
  mgmtType: 'mgmtType',
  listingStatus: 'listingStatus',
} as const

const allStatusOption = {
  option: 'isAll',
  optionMeta: { id: 'isAll', displayText: 'All' },
}

const listingAllStatusOption = {
  option: 'isAll',
  optionMeta: { id: 'listingStatusIsAll', displayText: 'All' },
}

type FilterOption = {
  option: string
  optionMeta: { id: string; displayText: string }
}

type FilterOptionMap = { [key: string]: FilterOption }

type MgmtTypeFilterValues = Record<string, FilterOptionMap | null | undefined>

const getFilterOptionMap = (
  filterValues: MgmtTypeFilterValues | null | undefined,
  field: string,
): FilterOptionMap => filterValues?.[field] || {}

const removeFilterOptions = (
  values: FilterOptionMap,
  optionIds: string[],
): FilterOptionMap =>
  Object.entries(values).reduce<FilterOptionMap>((acc, [optionId, option]) => {
    if (!optionIds.includes(optionId)) {
      acc[optionId] = option
    }
    return acc
  }, {})

export const listingStatusActionsKeys = {
  isAll: 'isAll',
  live: 'live',
  live_off_market: 'live_off_market',
  leased: 'leased',
}

export const listingStatusActions = {
  isAll: 'All',
  live: 'Live',
  live_off_market: 'Live Off Market',
  leased: 'Leased',
}

export const getListingStatusFilterState = (
  filterValues: MgmtTypeFilterValues | null | undefined,
) =>
  Object.values(
    getFilterOptionMap(filterValues, mgmtTypeFilterFields.listingStatus),
  )
    .map((value) => value.option)
    .filter((listingStatus) => listingStatus !== listingStatusActionsKeys.isAll)

export const mgmtTypeFilterDataExtractor = (
  filterValues: MgmtTypeFilterValues | null | undefined,
) => {
  return {
    mgmtType: Object.values(
      getFilterOptionMap(filterValues, mgmtTypeFilterFields.mgmtType),
    ).map((value) => value.option),
    listingStatus: getListingStatusFilterState(filterValues),
  }
}

export const mgmtTypeActionsKeys = {
  isAll: 'isAll',
  isNewMgmt: 'isNewMgmt',
  isRelet: 'isRelet',
  isNA: 'isNA',
}

export const mgmtTypeActions = {
  isAll: 'All',
  isNA: 'N/A',
  isNewMgmt: 'New Management',
  isRelet: 'Re-let',
}

export const generateListingStatusFilterData = ({
  listingStatus,
}: {
  listingStatus?: string | string[]
}) => {
  const selectedListingStatuses = Array.isArray(listingStatus)
    ? listingStatus
    : listingStatus
    ? [listingStatus]
    : []

  const validListingStatuses = Object.values(listingStatusActionsKeys).filter(
    (status) => status !== listingStatusActionsKeys.isAll,
  )

  return selectedListingStatuses
    .filter((status) => validListingStatuses.includes(status))
    .reduce<FilterOptionMap>((acc, status) => {
      acc[status] = {
        option: status,
        optionMeta: {
          id: status,
          displayText:
            listingStatusActions[status as keyof typeof listingStatusActions],
        },
      }
      return acc
    }, {})
}

export const MgmtFilAppliedFilterRenderer = ({
  filterValues,
}: {
  filterValues: MgmtTypeFilterValues | null | undefined
}) => {
  const listingStatusText = Object.values(
    getFilterOptionMap(filterValues, mgmtTypeFilterFields.listingStatus),
  )
    .map((actionOption) => actionOption.optionMeta.displayText)
    .join(', ')
  const mgmtTypeText = Object.values(
    getFilterOptionMap(filterValues, mgmtTypeFilterFields.mgmtType),
  )
    .map((actionOption) => actionOption.optionMeta.displayText)
    .join(', ')

  return [listingStatusText, mgmtTypeText].filter(Boolean).join(', ')
}

export const isListingStatusFilterApplied = (
  mgmtTypeFilterValues: MgmtTypeFilterValues | null | undefined,
) => {
  return Object.values(
    getFilterOptionMap(
      mgmtTypeFilterValues,
      mgmtTypeFilterFields.listingStatus,
    ),
  ).length
}

export const isMgmtTypeFilterApplied = (
  mgmtTypeFilterValues: MgmtTypeFilterValues | null | undefined,
) => {
  return (
    isListingStatusFilterApplied(mgmtTypeFilterValues) ||
    Object.values(
      getFilterOptionMap(mgmtTypeFilterValues, mgmtTypeFilterFields.mgmtType),
    ).length
  )
}

interface FormBag {
  values: MgmtTypeFilterValues
  setFieldValue: (field: string, value: FilterOptionMap) => void
  setValues: (values: MgmtTypeFilterValues) => void
}

export const MgmtTypeFilterBody = ({
  formBag: { values, setFieldValue, setValues },
  includeListingStatusFilter = false,
  includeFilterRelets = true,
}: {
  formBag: FormBag
  includeListingStatusFilter?: boolean
  includeFilterRelets?: boolean
}) => {
  const options = Object.entries(mgmtTypeActions).map(([id, label]) => {
    return {
      option: id,
      optionMeta: {
        id,
        displayText: label,
      },
    }
  })

  const selectedValues = values[mgmtTypeFilterFields.mgmtType] || {}
  const selectedListingStatusValues =
    values[mgmtTypeFilterFields.listingStatus] || {}

  const setFilterFieldValue = (field: string, value: FilterOptionMap) => {
    if (setValues) {
      setValues({ ...values, [field]: value })
      return
    }

    setFieldValue(field, value)
  }

  const managementOptionIds = Object.values(mgmtTypeActionsKeys).filter(
    (mgmtType) => mgmtType !== mgmtTypeActionsKeys.isAll,
  )
  const listingStatusOptionIds = Object.values(listingStatusActionsKeys).filter(
    (listingStatus) => listingStatus !== listingStatusActionsKeys.isAll,
  )

  const reconcileAllOption = (
    nextValues: FilterOptionMap,
    optionIds: string[],
    allOption = allStatusOption,
  ) => {
    const allOptionsSelected = optionIds.every(
      (optionId) => nextValues[optionId],
    )
    const allOptionId = allOption.optionMeta.id

    if (allOptionsSelected) {
      return {
        ...nextValues,
        [allOptionId]: allOption,
      }
    }

    return removeFilterOptions(nextValues, [allOptionId])
  }

  const sourcesOptions = Object.values(mgmtTypeActionsKeys).map((mgmtType) => ({
    option: mgmtType,
    optionMeta: {
      id: mgmtType,
      displayText: mgmtTypeActions[mgmtType as keyof typeof mgmtTypeActions],
    },
  }))

  const listingStatusOptions = [
    listingAllStatusOption,
    ...Object.values(listingStatusActionsKeys)
      .filter(
        (listingStatus) => listingStatus !== listingStatusActionsKeys.isAll,
      )
      .map((listingStatus) => ({
        option: listingStatus,
        optionMeta: {
          id: listingStatus,
          displayText:
            listingStatusActions[
              listingStatus as keyof typeof listingStatusActions
            ],
        },
      })),
  ]

  const toggleAllListingStatuses = (checked: boolean) => {
    if (checked) {
      setFilterFieldValue(
        mgmtTypeFilterFields.listingStatus,
        Object.assign(
          {},
          ...listingStatusOptions.map((option) => ({
            [option.optionMeta.id]: option,
          })),
        ),
      )
    } else {
      setFilterFieldValue(mgmtTypeFilterFields.listingStatus, {})
    }
  }

  const toggleAll = (checked: boolean) => {
    if (checked) {
      setFilterFieldValue(
        mgmtTypeFilterFields.mgmtType,
        Object.assign(
          {},
          ...sourcesOptions.map((option) => ({
            [option.optionMeta.id]: option,
          })),
        ),
      )
    } else {
      setFilterFieldValue(mgmtTypeFilterFields.mgmtType, {})
    }
  }

  const onCheckboxClicked = (checked: boolean, option: FilterOption) => {
    if (option.optionMeta.id === mgmtTypeActionsKeys.isAll) {
      toggleAll(checked)
      return
    }
    if (checked) {
      const allFilters = {
        ...selectedValues,
        [option.optionMeta.id]: option,
      }
      setFilterFieldValue(mgmtTypeFilterFields.mgmtType, {
        ...reconcileAllOption(allFilters, managementOptionIds),
      })
    } else {
      const otherOptions = removeFilterOptions(selectedValues, [
        option.optionMeta.id,
        allStatusOption.optionMeta.id,
      ])
      setFilterFieldValue(mgmtTypeFilterFields.mgmtType, {
        ...otherOptions,
      })
    }
  }

  const onListingStatusCheckboxClicked = (
    checked: boolean,
    option: FilterOption,
  ) => {
    if (option.option === listingStatusActionsKeys.isAll) {
      toggleAllListingStatuses(checked)
      return
    }
    if (checked) {
      setFilterFieldValue(mgmtTypeFilterFields.listingStatus, {
        ...reconcileAllOption(
          {
            ...selectedListingStatusValues,
            [option.optionMeta.id]: option,
          },
          listingStatusOptionIds,
          listingAllStatusOption,
        ),
      })
    } else {
      const otherOptions = removeFilterOptions(selectedListingStatusValues, [
        option.optionMeta.id,
        listingAllStatusOption.optionMeta.id,
      ])
      setFilterFieldValue(mgmtTypeFilterFields.listingStatus, {
        ...otherOptions,
      })
    }
  }

  return (
    <Box minWidth="200px" maxHeight="300px" overflowY="auto">
      {includeListingStatusFilter && (
        <>
          <DropdownBodySectionHeader
            header="Listing"
            containerProps={{ mb: theme.space[4] + 'px' }}
          />

          <CheckboxesList
            options={listingStatusOptions}
            checkedOptions={selectedListingStatusValues}
            onCheckBoxClicked={onListingStatusCheckboxClicked}
          />
        </>
      )}

      {includeFilterRelets && (
        <>
          <DropdownBodySectionHeader
            header="Management"
            containerProps={{ mb: theme.space[4] + 'px' }}
          />

          <CheckboxesList
            options={options}
            checkedOptions={selectedValues}
            onCheckBoxClicked={onCheckboxClicked}
          />
        </>
      )}
    </Box>
  )
}
